import { Timestamp } from 'firebase/firestore';
import { SUBSCRIPTION_TIERS, SUBSCRIPTION_PLANS, SubscriptionTierType, resolveTier } from './SubscriptionConfig';
export { resolveTier };

/**
 * Organization details for business accounts.
 * Matches Firestore `users/{uid}.organizationDetails` schema.
 */
export interface OrganizationDetails {
  name: string;
  description?: string;
  address?: string;
  phone?: string;
}

/**
 * User profile stored in Firestore `users/{uid}`.
 * Mirrors the iOS app's User model and Firestore security rules.
 */
export interface UserProfile {
  id?: string;
  firstName: string;
  lastName: string;
  email: string;
  accountType?: 'personal' | 'business';
  organizationDetails?: OrganizationDetails;
  isOnboardingCompleted: boolean;
  photoURLSmall?: string;
  photoURLMedium?: string;
  photoURLLarge?: string;
  createdAt?: Timestamp;
  subscription?: Subscription;
}

/**
 * Subscription record stored in Firestore `subscriptions/{uid}`.
 * Mirrors the iOS app's Subscription model.
 */
export interface Subscription {
  // Allow string for legacy 'basic' or future tiers, but usually one of the known types
  tier: SubscriptionTierType;
  status: 'active' | 'expired' | 'trial';
  period?: 'monthly' | 'yearly';
  productId?: string;
  startDate?: Timestamp;
  endDate?: Timestamp;
  isTrial?: boolean;
  autoRenew?: boolean;
  catalogsCreated: number;
  objectsUploaded: number;
  arViewsUsed: number;
  catalogLimitCutoff?: Timestamp;
  itemLimitCutoff?: Timestamp;
  // Dynamic Limits (Backfilled by backend)
  maxCatalogs?: number;
  maxObjects?: number;
  maxViews?: number;
  userId?: string;
  originalTransactionId?: string;
  transactionId?: string;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

/** Tier limits matching the iOS app */
// Re-export or forward the config
export const TIER_LIMITS = SUBSCRIPTION_TIERS;

/** Plan details for display — naming matches the new config */
export const PLAN_DETAILS = SUBSCRIPTION_PLANS;

/** Check if user has a business account type */
export const isBusinessUser = (user: UserProfile): boolean => {
  return user.accountType === 'business';
};

/** Get formatted display name */
export const getDisplayName = (user: UserProfile): string => {
  return `${user.firstName} ${user.lastName}`.trim();
};

/** Get business name or fall back to display name */
export const getBusinessName = (user: UserProfile): string => {
  return user.organizationDetails?.name || getDisplayName(user);
};

/** Get best available profile photo URL */
export const getBestProfilePhoto = (user: UserProfile): string | undefined => {
  return user.photoURLMedium ?? user.photoURLSmall ?? user.photoURLLarge;
};
